import { useState } from "react";
import { ProgressBarSteps } from "./ProgressBarSteps";
import ProfilePictureStep from "./steps/ProfilePictureStep";
import AgentInfoStep from "./steps/AgentInfoStep";
import CoreIdentityStep from "./steps/CoreIdentityStep";
import LoadingScreen from "./LoadingScreen";
import SuccessScreen from "./SuccessScreen";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useAuthContext } from "@/components/AuthorizationProvider";
import { getDefaultPersonaForUser } from "@/lib/persona";
import { createPersona, PersonaDocuments, uploadAvatar } from "@/api/api.ts";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { Persona } from "@/api/personas";

// Custom progress bar navigation handler function
const handleProgressNavigation = (
  newStep: number,
  currentStep: number,
  getValues: any,
  toast: any,
) => {
  // Prevent backward navigation or lateral navigation to the same step
  if (newStep <= currentStep) {
    return newStep;
  }

  // Validate based on current step
  if (currentStep === 0) {
    // First step (profile picture) validation
    const { profileImage, avatarFile } = getValues();
    if (!profileImage && !avatarFile) {
      toast({
        description: "Please upload a profile picture before proceeding",
        variant: "destructive",
      });
      return currentStep;
    }
  } else if (currentStep === 1) {
    // Second step (agent info) validation
    const { name, bio } = getValues();
    if (!name || !bio) {
      toast({
        description: "Please complete the agent information before proceeding",
        variant: "destructive",
      });
      return currentStep;
    }
  }

  return newStep;
};

const rightText = [
  {
    title: "CREATE AN AGENT OF THE FUTURE",
    description:
      "Memetica agents are next-level AI, designed to think, adapt, and perform with unmatched intelligence. Unlock powerful capabilities you won't find anywhere else.",
  },
  {
    title: "GIVE YOUR AGENT A NAME & IDENTITY",
    description:
      "Every great AI starts with a name. Define your agent's personality with a unique name and a short bio. Whether it's a problem-solver, a creative thinker, or a data-driven powerhouse, shape its identity and set the stage for its capabilities.",
  },
  {
    title: "DEFINE YOUR AGENT'S CORE IDENTITY",
    description:
      "Shape your AI's personality and interests. Is it analytical or creative? Curious or decisive? Define its core traits to make it truly unique. Every detail helps craft an intelligent agent that aligns with your vision and purpose.",
  },
];

// Schema for the ProfilePictureStep
const profilePictureSchema = z.object({
  avatarFile: z.instanceof(File).optional(),
  profileImage: z.string().optional(),
});

// Schema for the AgentInfoStep
const agentInfoSchema = z.object({
  name: z.string().min(1, "Name is required"),
  bio: z.string().min(1, "Bio is required"),
});

// Schema for the CoreIdentityStep
const coreIdentitySchema = z.object({
  interests: z.array(z.string()).min(1, "At least one interest is required"),
  personality: z.string().min(1, "Personality is required"),
});

// Combined schemas
const createPersonaSchema = profilePictureSchema
  .merge(agentInfoSchema)
  .merge(coreIdentitySchema);

type CreatePersonaFormValues = z.infer<typeof createPersonaSchema>;

export const CreatePersonaFlow = () => {
  const [step, setStep] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const [createdPersona, setCreatedPersona] = useState<Persona | undefined>(
    undefined,
  );
  const { userAuthorization, setForceRefresh } = useAuthContext();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const methods = useForm<CreatePersonaFormValues>({
    resolver: zodResolver(createPersonaSchema),
    defaultValues: {
      avatarFile: undefined,
      profileImage: undefined,
      name: "",
      bio: "",
      interests: [],
      personality: "",
    },
  });

  const handleSetStep = (newStep: number) => {
    const validatedStep = handleProgressNavigation(
      newStep,
      step,
      methods.getValues,
      toast,
    );
    setStep(validatedStep);
  };

  const handleCreatePersona = async (data: CreatePersonaFormValues) => {
    try {
      setLoading(true);

      // Get default persona structure from user authorization
      const personaDefaults = getDefaultPersonaForUser(userAuthorization);

      // Prepare persona data
      const personaData = {
        ...personaDefaults,
        name: data.name,
        short_bio: data.bio,
        persona: data.personality,
        interests: data.interests,
        persona_id: crypto.randomUUID(),
      };

      // Prepare documents data
      const documents: PersonaDocuments = {
        dynamic_web_links: [],
        static_web_links: [],
        google_drive_folders: [],
        github_repos: [],
        core_personality: [],
        static_data: [],
        live_data: [],
        tools: [],
        knowledge_graph_status: "",
        last_built: null,
      };

      let avatarPath = null;

      // Upload avatar if available
      if (data.avatarFile) {
        try {
          const uploadResponse = await uploadAvatar(data.avatarFile);
          const avatarId = uploadResponse.file;
          avatarPath = uploadResponse.file_path;
          personaData.avatar_id = avatarId;
        } catch (error) {
          console.error("Error uploading avatar:", error);
          toast({ description: "Error uploading avatar" });
        }
      }

      // Create the persona
      const createdPersona = await createPersona(
        personaData,
        documents,
        userAuthorization.qs_username,
        avatarPath,
      );

      await queryClient.invalidateQueries({ queryKey: ["personas"] });

      // @ts-expect-error I don't think this property exists
      setForceRefresh(!userAuthorization.forceRefresh);

      // Show success screen
      setCreatedPersona(createdPersona);
      setShowSuccess(true);
    } catch (error) {
      console.error("Error creating persona:", error);
      toast({ description: "Error creating agent" });
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <LoadingScreen isVisible={loading} />
      <SuccessScreen isVisible={showSuccess} persona={createdPersona} />
      <FormProvider {...methods}>
        <div className="flex flex-col md:flex-row w-full min-h-screen bg-[#111111]">
          <div className="w-full md:w-1/2 p-4 md:p-8 md:pt-16 flex flex-col justify-around">
            <ProgressBarSteps step={step} setStep={handleSetStep} />

            <div className="flex flex-col">
              <div className="text-white flex-1">
                {step === 0 && (
                  <ProfilePictureStep step={step} setStep={handleSetStep} />
                )}
                {step === 1 && <AgentInfoStep setStep={handleSetStep} />}
                {step === 2 && (
                  <CoreIdentityStep
                    setStep={handleSetStep}
                    onSubmit={methods.handleSubmit(handleCreatePersona)}
                  />
                )}
              </div>
            </div>
          </div>

          <div className="hidden md:block md:w-1/2 relative overflow-hidden md:min-h-0">
            <div
              className="absolute inset-0 bg-center bg-no-repeat bg-cover opacity-20"
              style={{
                backgroundImage: `url(/create-persona/step${step + 1}.png)`,
              }}
            ></div>

            <div className="absolute inset-0 flex flex-col items-center justify-center px-6 md:px-12 z-10 py-8 md:py-0">
              <div className="text-center md:max-w-md">
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 md:mb-6 text-left">
                  {rightText[step].title}
                </h2>
                <p className="text-gray-300 text-base md:text-lg text-left">
                  {rightText[step].description}
                </p>
              </div>
            </div>
          </div>
        </div>
      </FormProvider>
    </>
  );
};
